import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getRoReportingGroups, markAsReportedToRo, type RoReportingGroup, type RoReportingQueryParams } from "../api/roReporting";
import { useTelemetry } from "../shared/useTelemetry";
import "./ro-reporting-page.css";

export const RoReportingPage = (): JSX.Element => {
    const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
    const [filters, setFilters] = useState<RoReportingQueryParams>({});
    const queryClient = useQueryClient();
    const telemetry = useTelemetry();

    const { data: groups, isLoading, error } = useQuery({
        queryKey: ["ro-reporting", filters],
        queryFn: () => getRoReportingGroups(filters),
        staleTime: 30 * 1000
    });

    const markAsReportedMutation = useMutation({
        mutationFn: markAsReportedToRo,
        onSuccess: () => {
            telemetry.track("ro-reporting.marked");
            queryClient.invalidateQueries({ queryKey: ["ro-reporting"] });
            setSelectedGroups(new Set());
        },
        onError: (error: unknown) => {
            telemetry.trackError("ro-reporting.markFailed", error);
        }
    });

    const handleToggleGroup = (groupKey: string) => {
        const newSelected = new Set(selectedGroups);
        if (newSelected.has(groupKey)) {
            newSelected.delete(groupKey);
        } else {
            newSelected.add(groupKey);
        }
        setSelectedGroups(newSelected);
    };

    const handleSelectAll = () => {
        if (!groups) return;
        const allKeys = groups
            .filter(g => !g.isReportedToRo)
            .map(g => getGroupKey(g));
        setSelectedGroups(new Set(allKeys));
    };

    const handleDeselectAll = () => {
        setSelectedGroups(new Set());
    };

    const handleMarkAsReported = () => {
        if (selectedGroups.size === 0) return;

        const deliveryIds: string[] = [];
        groups?.forEach(group => {
            const groupKey = getGroupKey(group);
            if (selectedGroups.has(groupKey)) {
                deliveryIds.push(...group.deliveryIds);
            }
        });

        if (deliveryIds.length > 0) {
            markAsReportedMutation.mutate({ deliveryIds });
        }
    };

    const getGroupKey = (group: RoReportingGroup): string => {
        return `${group.clientRequestId}-${group.electoralDistrictId || "none"}`;
    };

    if (isLoading) {
        return <div className="ro-reporting-page__loading">Laddar leveranser...</div>;
    }

    if (error) {
        return <div className="ro-reporting-page__error">Fel vid laddning: {(error as Error).message}</div>;
    }

    const unreportedGroups = groups?.filter(g => !g.isReportedToRo) ?? [];
    const reportedGroups = groups?.filter(g => g.isReportedToRo) ?? [];

    return (
        <div className="ro-reporting-page">
            <h1>RO-rapportering</h1>
            <p className="ro-reporting-page__description">
                Gruppera och markera leveranser som rapporterade till RO-appen. 
                Leveranser grupperas per session (ClientRequestId) och valdistrikt.
            </p>

            <div className="ro-reporting-page__filters">
                <div className="filter-group">
                    <label>
                        Kampanj ID:
                        <input
                            type="text"
                            value={filters.campaignId || ""}
                            onChange={(e) => setFilters({ ...filters, campaignId: e.target.value || undefined })}
                            placeholder="GUID"
                        />
                    </label>
                </div>
                <div className="filter-group">
                    <label>
                        Session ID:
                        <input
                            type="text"
                            value={filters.clientRequestId || ""}
                            onChange={(e) => setFilters({ ...filters, clientRequestId: e.target.value || undefined })}
                            placeholder="ClientRequestId"
                        />
                    </label>
                </div>
                <div className="filter-group">
                    <label>
                        Status:
                        <select
                            value={filters.isReportedToRo === undefined ? "all" : filters.isReportedToRo.toString()}
                            onChange={(e) => {
                                const value = e.target.value;
                                setFilters({
                                    ...filters,
                                    isReportedToRo: value === "all" ? undefined : value === "true"
                                });
                            }}
                        >
                            <option value="all">Alla</option>
                            <option value="false">Ej rapporterade</option>
                            <option value="true">Rapporterade</option>
                        </select>
                    </label>
                </div>
            </div>

            {unreportedGroups.length > 0 && (
                <div className="ro-reporting-page__section">
                    <div className="section-header">
                        <h2>Ej rapporterade ({unreportedGroups.length})</h2>
                        <div className="section-actions">
                            <button onClick={handleSelectAll} className="button-secondary">
                                Välj alla
                            </button>
                            <button onClick={handleDeselectAll} className="button-secondary">
                                Avmarkera alla
                            </button>
                            <button
                                onClick={handleMarkAsReported}
                                disabled={selectedGroups.size === 0 || markAsReportedMutation.isPending}
                                className="button-primary"
                            >
                                {markAsReportedMutation.isPending
                                    ? "Markerar..."
                                    : `Markera som rapporterade (${selectedGroups.size})`}
                            </button>
                        </div>
                    </div>
                    <div className="groups-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>
                                        <input
                                            type="checkbox"
                                            checked={unreportedGroups.length > 0 && unreportedGroups.every(g => selectedGroups.has(getGroupKey(g)))}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    handleSelectAll();
                                                } else {
                                                    handleDeselectAll();
                                                }
                                            }}
                                        />
                                    </th>
                                    <th>Session ID</th>
                                    <th>Valdistrikt</th>
                                    <th>Kampanj</th>
                                    <th>Leveranser</th>
                                    <th>Adresser</th>
                                    <th>Antal flygblad</th>
                                    <th>Första leverans</th>
                                    <th>Sista leverans</th>
                                </tr>
                            </thead>
                            <tbody>
                                {unreportedGroups.map(group => {
                                    const groupKey = getGroupKey(group);
                                    return (
                                        <tr key={groupKey}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedGroups.has(groupKey)}
                                                    onChange={() => handleToggleGroup(groupKey)}
                                                />
                                            </td>
                                            <td className="monospace">{group.clientRequestId}</td>
                                            <td>
                                                {group.electoralDistrictName || group.electoralDistrictCode || "Okänt"}
                                            </td>
                                            <td>{group.campaignName}</td>
                                            <td>{group.deliveryCount}</td>
                                            <td>{group.addressCount}</td>
                                            <td>{group.totalQuantity}</td>
                                            <td>{new Date(group.firstDeliveredAt).toLocaleString("sv-SE")}</td>
                                            <td>{new Date(group.lastDeliveredAt).toLocaleString("sv-SE")}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {reportedGroups.length > 0 && (
                <div className="ro-reporting-page__section">
                    <h2>Rapporterade ({reportedGroups.length})</h2>
                    <div className="groups-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Session ID</th>
                                    <th>Valdistrikt</th>
                                    <th>Kampanj</th>
                                    <th>Leveranser</th>
                                    <th>Adresser</th>
                                    <th>Antal flygblad</th>
                                    <th>Rapporterad</th>
                                    <th>Rapporterad av</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportedGroups.map(group => {
                                    const groupKey = getGroupKey(group);
                                    return (
                                        <tr key={groupKey} className="reported-row">
                                            <td className="monospace">{group.clientRequestId}</td>
                                            <td>
                                                {group.electoralDistrictName || group.electoralDistrictCode || "Okänt"}
                                            </td>
                                            <td>{group.campaignName}</td>
                                            <td>{group.deliveryCount}</td>
                                            <td>{group.addressCount}</td>
                                            <td>{group.totalQuantity}</td>
                                            <td>
                                                {group.reportedToRoAt
                                                    ? new Date(group.reportedToRoAt).toLocaleString("sv-SE")
                                                    : "-"}
                                            </td>
                                            <td>{group.reportedToRoByUserName || "-"}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {groups && groups.length === 0 && (
                <div className="ro-reporting-page__empty">
                    Inga leveranser hittades med de valda filtren.
                </div>
            )}
        </div>
    );
};
